import { useState, useEffect } from "react";
import { Box, Container, styled, Alert } from "@mui/material";
import { Target, Dumbbell, TrendingUp, Heart, User, Zap, Activity, ArrowRight, CheckCircle2, Lock } from "lucide-react";
import { API_ENDPOINTS } from "config/api";
import PaywallPage from "./PaywallPage";

const QuizContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 2),
  background: "transparent",
  position: "relative",

  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2, 2, 6, 2),
  },
}));

const ProgressBar = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),

  [theme.breakpoints.down("sm")]: {
    marginBottom: theme.spacing(2),
  },
}));

const ProgressInfo = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  marginBottom: "8px",
  color: "rgba(255, 255, 255, 0.6)",
  fontSize: "0.875rem",
  fontFamily: "'Oswald', sans-serif",
  fontWeight: 500,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
});

const ProgressTrack = styled(Box)({
  height: "8px",
  background: "rgba(255, 255, 255, 0.1)",
  borderRadius: "999px",
  overflow: "hidden",
});

const ProgressFill = styled(Box)(({ progress }) => ({
  height: "100%",
  width: `${progress}%`,
  background: "#FF6B35",
  borderRadius: "999px",
  transition: "width 0.3s ease",
}));

const QuestionTitle = styled("h2")(({ theme }) => ({
  color: "#ffffff",
  fontSize: "2.5rem",
  fontWeight: 700,
  textAlign: "center",
  marginBottom: theme.spacing(1),
  fontFamily: "'Oswald', sans-serif",
  textTransform: "uppercase",
  letterSpacing: "-0.02em",

  [theme.breakpoints.down("sm")]: {
    fontSize: "1.75rem",
    marginBottom: theme.spacing(0.5),
  },
}));

const QuestionSubtitle = styled("p")(({ theme }) => ({
  color: "rgba(255, 255, 255, 0.6)",
  fontSize: "1rem",
  textAlign: "center",
  marginBottom: "32px",
  fontFamily: "'Oswald', sans-serif",
  fontWeight: 400,
  letterSpacing: "0.03em",

  [theme.breakpoints.down("sm")]: {
    fontSize: "0.875rem",
    marginBottom: "16px",
  },
}));

const OptionsGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: theme.spacing(2),

  [theme.breakpoints.down("sm")]: {
    gap: theme.spacing(1.5),
  },

  "@media (min-width: 640px)": {
    gridTemplateColumns: "repeat(2, 1fr)",
  },
}));

const OptionButton = styled("button")(({ theme, selected, locked }) => ({
  padding: theme.spacing(3),
  borderRadius: "12px",
  border: `2px solid ${selected ? "#FF6B35" : "rgba(255, 255, 255, 0.1)"}`,
  background: selected ? "rgba(255, 107, 53, 0.15)" : "rgba(255, 255, 255, 0.03)",
  cursor: locked ? "not-allowed" : "pointer",
  transition: "all 0.3s ease",
  textAlign: "left",
  fontFamily: "'Oswald', sans-serif",
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  backdropFilter: "blur(10px)",
  opacity: locked ? 0.7 : 1,
  position: "relative",

  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
    gap: theme.spacing(1.5),
  },

  "&:hover": {
    borderColor: locked ? "rgba(255, 255, 255, 0.1)" : "#FF6B35",
    background: locked ? "rgba(255, 255, 255, 0.03)" : (selected ? "rgba(255, 107, 53, 0.2)" : "rgba(255, 255, 255, 0.05)"),
  },

  "&:active": {
    transform: locked ? "none" : "scale(0.98)",
  },
}));

const IconBox = styled(Box)(({ selected, theme }) => ({
  width: "48px",
  height: "48px",
  borderRadius: "8px",
  background: selected ? "#FF6B35" : "rgba(255, 255, 255, 0.1)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,

  [theme.breakpoints.down("sm")]: {
    width: "40px",
    height: "40px",
  },
}));

const OptionText = styled(Box)(({ selected }) => ({
  flex: 1,
  color: selected ? "#FF6B35" : "#ffffff",
  fontSize: "1rem",
  fontWeight: 600,
  fontFamily: "'Oswald', sans-serif",
  letterSpacing: "0.03em",
  textTransform: "uppercase",
}));

const OptionDescription = styled(Box)({
  color: "rgba(255, 255, 255, 0.5)",
  fontSize: "0.75rem",
  marginTop: "2px",
  fontFamily: "'Oswald', sans-serif",
  fontWeight: 400,
  textTransform: "none",
});

const CheckIcon = styled(Box)({
  width: "24px",
  height: "24px",
  color: "#FF6B35",
  flexShrink: 0,
});

const ProBadge = styled(Box)({
  position: "absolute",
  top: "8px",
  right: "8px",
  background: "linear-gradient(135deg, #FFD700, #FFA500)",
  color: "#000",
  fontSize: "0.65rem",
  fontWeight: 700,
  padding: "4px 8px",
  borderRadius: "4px",
  fontFamily: "'Oswald', sans-serif",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  zIndex: 10,
  boxShadow: "0 2px 8px rgba(255, 215, 0, 0.4)",
});

const LockIconWrapper = styled(Box)({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  zIndex: 10,
  background: "rgba(0, 0, 0, 0.8)",
  borderRadius: "50%",
  width: "48px",
  height: "48px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "2px solid rgba(255, 215, 0, 0.6)",
});

const ContinueHint = styled(Box)({
  marginTop: "24px",
  textAlign: "center",
  color: "rgba(255, 255, 255, 0.5)",
  fontSize: "0.875rem",
  fontFamily: "'Oswald', sans-serif",
});

const EmailFormCard = styled(Box)(({ theme }) => ({
  background: "rgba(255, 255, 255, 0.03)",
  border: "2px solid rgba(255, 255, 255, 0.1)",
  borderRadius: "12px",
  padding: theme.spacing(4),
  maxWidth: "480px",
  margin: "0 auto",
  backdropFilter: "blur(10px)",

  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(3),
  },
}));

const Input = styled("input")(({ theme }) => ({
  width: "100%",
  padding: theme.spacing(2),
  border: "2px solid rgba(255, 255, 255, 0.1)",
  background: "rgba(255, 255, 255, 0.05)",
  borderRadius: "8px",
  fontSize: "1rem",
  color: "#ffffff",
  fontFamily: "'Oswald', sans-serif",
  fontWeight: 400,
  outline: "none",
  transition: "all 0.3s ease",

  "&::placeholder": {
    color: "rgba(255, 255, 255, 0.4)",
  },

  "&:focus": {
    borderColor: "#FF6B35",
    boxShadow: "0 0 0 3px rgba(255, 107, 53, 0.2)",
    background: "rgba(255, 255, 255, 0.08)",
  },
}));

const Label = styled("label")({
  display: "block",
  color: "#ffffff",
  fontSize: "0.875rem",
  marginBottom: "8px",
  fontWeight: 600,
  fontFamily: "'Oswald', sans-serif",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
});

const SubmitButton = styled("button")(({ theme, disabled }) => ({
  width: "100%",
  padding: theme.spacing(2, 4),
  marginTop: theme.spacing(2),
  background: disabled ? "rgba(255, 255, 255, 0.2)" : "#FF6B35",
  color: "#ffffff",
  border: "none",
  borderRadius: "8px",
  fontSize: "1rem",
  fontWeight: 700,
  cursor: disabled ? "not-allowed" : "pointer",
  transition: "all 0.3s ease",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1),
  fontFamily: "'Oswald', sans-serif",
  textTransform: "uppercase",
  letterSpacing: "0.1em",
  boxShadow: disabled ? "none" : "0 10px 15px -3px rgba(255, 107, 53, 0.3)",

  "@keyframes spin": {
    from: {
      transform: "rotate(0deg)",
    },
    to: {
      transform: "rotate(360deg)",
    },
  },

  "& .spinning-icon": {
    animation: "spin 1s linear infinite",
  },

  "&:hover": {
    transform: disabled ? "none" : "translateY(-2px)",
    background: disabled ? "rgba(255, 255, 255, 0.2)" : "#E85A2A",
    boxShadow: disabled ? "none" : "0 20px 25px -5px rgba(255, 107, 53, 0.4)",
  },

  "&:active": {
    transform: disabled ? "none" : "scale(0.98)",
  },
}));

const BackButton = styled("button")(({ theme }) => ({
  color: "rgba(255, 255, 255, 0.6)",
  fontSize: "0.875rem",
  margin: "0 auto",
  display: "block",
  marginTop: theme.spacing(3),
  background: "none",
  border: "none",
  cursor: "pointer",
  transition: "color 0.3s ease",
  fontFamily: "'Oswald', sans-serif",
  fontWeight: 500,
  letterSpacing: "0.05em",
  textTransform: "uppercase",

  "&:hover": {
    color: "#FF6B35",
  },
}));

const SuccessIcon = styled(Box)({
  width: "64px",
  height: "64px",
  background: "#FF6B35",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto",
  marginBottom: "16px",
});

const SuccessTitle = styled("h3")(({ theme }) => ({
  color: "#ffffff",
  fontSize: "2rem",
  fontWeight: 700,
  textAlign: "center",
  marginBottom: theme.spacing(2),
  fontFamily: "'Oswald', sans-serif",
  textTransform: "uppercase",
  letterSpacing: "-0.02em",
}));

const SuccessText = styled("p")({
  color: "rgba(255, 255, 255, 0.8)",
  fontSize: "1rem",
  textAlign: "center",
  marginBottom: "24px",
  fontFamily: "'Oswald', sans-serif",
  fontWeight: 400,
});

function QuizSection() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    goal: "",
    level: "",
    equipment: "",
    schedule: "",
    email: "",
  });
  const [showPaywall, setShowPaywall] = useState(false);
  const [error, setError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPremiumUser, setIsPremiumUser] = useState(false);
  const [premiumUserEmail, setPremiumUserEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Helper function to get cookie value
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };

  // Helper function to set cookie
  const setCookie = (name, value, days = 365) => {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = `expires=${date.toUTCString()}`;
    document.cookie = `${name}=${value};${expires};path=/`;
  };

  // Detect and verify token on mount (for paid users returning via email link)
  useEffect(() => {
    const checkPremiumStatus = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const tokenFromUrl = urlParams.get('token');
      const tokenFromCookie = getCookie('access_token');
      const token = tokenFromUrl || tokenFromCookie;

      if (token) {
        try {
          const response = await fetch(API_ENDPOINTS.verifyToken, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ token }),
          });

          const data = await response.json();

          if (data.success && data.valid && data.tier === 'paid') {
            setIsPremiumUser(true);
            if (data.email) {
              setPremiumUserEmail(data.email);
            }
            if (tokenFromUrl) {
              setCookie('access_token', token);
              if (window.gtag) {
                window.gtag('event', 'email_access_link_clicked', {
                  source: 'email',
                  user_tier: 'paid',
                  email: data.email
                });
              }
            }
          }
        } catch (error) {
          console.error('Token verification error:', error);
        }
      }
    };

    checkPremiumStatus();
  }, []);

  // Helper function to track events
  const trackEvent = (eventName, params = {}) => {
    if (window.gtag) {
      window.gtag("event", eventName, params);
    }
  };

  // Helper function to track Meta Pixel events
  const trackMetaPixel = (eventName, params = {}) => {
    if (window.fbq) {
      window.fbq("track", eventName, params);
    }
  };

  const totalSteps = 5;
  const progress = (step / totalSteps) * 100;

  // Track quiz start on mount
  useEffect(() => {
    trackEvent("quiz_started", {
      step: 1,
      step_name: "goal_selection",
      user_tier: isPremiumUser ? 'paid' : 'free'
    });
    trackMetaPixel("InitiateCheckout", {
      content_name: "AI Workout Quiz",
      content_category: "Fitness"
    });
  }, [isPremiumUser]);

  // Track when user reaches email step
  useEffect(() => {
    if (step === 5) {
      trackEvent("email_step_reached", {
        goal: formData.goal,
        level: formData.level,
        equipment: formData.equipment,
        schedule: formData.schedule,
        funnel_stage: "email_capture",
        user_tier: isPremiumUser ? 'paid' : 'free'
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const goals = [
    { id: "strength", label: "Max Strength", description: "Focus on nervous system & low reps", icon: Dumbbell },
    { id: "hypertrophy", label: "Hypertrophy", description: "Focus on volume & muscle size", icon: TrendingUp },
    { id: "endurance", label: "Fat Loss", description: "Focus on metabolic conditioning", icon: Heart },
    { id: "toned", label: "Body Recomposition", description: "Build muscle + lose fat simultaneously", icon: Target },
  ];

  const levels = [
    { id: "beginner", label: "Beginner", description: "0–5 Pull-ups • Foundation", icon: User },
    { id: "intermediate", label: "Intermediate", description: "6–15 Pull-ups • Dynamic Strength", icon: Activity },
    { id: "advanced", label: "Advanced", description: "15+ Pull-ups • Statics & Skills", icon: Zap },
  ];

  const equipmentOptions = [
    { id: "floor", label: "Floor Only", description: "No equipment needed", icon: Target, locked: false },
    { id: "pullup-bar", label: "Bar + Floor", description: "Implies a fuller workout", icon: Dumbbell, locked: false },
    { id: "rings", label: "Rings + Floor", description: "Advanced calisthenics setup", icon: TrendingUp, locked: false },
    { id: "parallettes", label: "Parallettes", description: "Parallel bars", icon: Activity, locked: false },
    { id: "bands", label: "Resistance Bands", description: "Portable band training", icon: Zap, locked: false },
    { id: "weighted-vest", label: "Weighted Vest", description: "Progressive overload", icon: Dumbbell, locked: false },
  ];

  const scheduleOptions = [
    { id: "2", label: "2 Days/Week", description: "Minimum effective dose", icon: Target },
    { id: "3", label: "3 Days/Week", description: "Recommended for beginners", icon: TrendingUp },
    { id: "4", label: "4 Days/Week", description: "Optimal for muscle growth", icon: Dumbbell },
    { id: "5", label: "5 Days/Week", description: "Advanced training split", icon: Zap },
  ];

  const handleGoalSelect = (goalId) => {
    setFormData({ ...formData, goal: goalId });
    trackEvent("goal_selected", { goal: goalId, step: 1, user_tier: isPremiumUser ? 'paid' : 'free' });
    setStep(2);
    setTimeout(() => {
      document.getElementById("quiz-section")?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const handleLevelSelect = (levelId) => {
    setFormData({ ...formData, level: levelId });
    trackEvent("level_selected", { level: levelId, step: 2, goal: formData.goal, user_tier: isPremiumUser ? 'paid' : 'free' });
    setStep(3);
    setTimeout(() => {
      document.getElementById("quiz-section")?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const handleEquipmentSelect = (equipmentId) => {
    setFormData({ ...formData, equipment: equipmentId });
    trackEvent("equipment_selected", { equipment: equipmentId, step: 3, goal: formData.goal, level: formData.level, user_tier: isPremiumUser ? 'paid' : 'free' });
    setStep(4);
    setTimeout(() => {
      document.getElementById("quiz-section")?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const handleScheduleSelect = async (scheduleId) => {
    const updatedFormData = { ...formData, schedule: scheduleId };
    setFormData(updatedFormData);

    trackEvent("schedule_selected", {
      schedule: scheduleId,
      step: 4,
      goal: formData.goal,
      level: formData.level,
      equipment: formData.equipment,
      user_tier: isPremiumUser ? 'paid' : 'free'
    });

    // If premium user, skip email step and generate PDF directly
    if (isPremiumUser && premiumUserEmail) {
      setIsSubmitting(true);
      const premiumFormData = { ...updatedFormData, email: premiumUserEmail };
      setFormData(premiumFormData);
      setTimeout(() => {
        handlePremiumUserGenerate(premiumFormData);
      }, 100);
    } else {
      setStep(5);
      setTimeout(() => {
        document.getElementById("quiz-section")?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  };

  // Handle PDF generation for premium users (paid users get unlimited regenerations)
  const handlePremiumUserGenerate = async (currentFormData) => {
    try {
      // Subscribe to Beehiiv
      await fetch(API_ENDPOINTS.subscribe, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: currentFormData.email,
          referring_site: "https://workoutaiplan.com",
          level: currentFormData.level,
          equipment: currentFormData.equipment,
          goal: currentFormData.goal,
        }),
      });

      // Generate PDF
      const pdfResponse = await fetch(API_ENDPOINTS.generateWorkout, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          level: currentFormData.level,
          equipment: currentFormData.equipment,
          goal: currentFormData.goal,
          schedule: currentFormData.schedule,
          email: currentFormData.email,
        }),
      });

      if (!pdfResponse.ok) {
        throw new Error("Failed to generate workout plan");
      }

      const blob = await pdfResponse.blob();
      const contentDisposition = pdfResponse.headers.get("Content-Disposition");
      let filename = "Workout-Plan.pdf";
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) filename = filenameMatch[1];
      }

      // Download immediately for paid users
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setSubmitted(true);

      trackEvent("paid_user_download", {
        goal: currentFormData.goal,
        level: currentFormData.level,
        equipment: currentFormData.equipment,
        user_tier: 'paid'
      });
    } catch (err) {
      console.error("Error:", err);
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle email submit - saves to DB as pending and shows paywall
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setEmailError("");
    setIsSubmitting(true);

    if (!validateEmail(formData.email)) {
      setEmailError("Please enter a valid email address");
      setError("Please enter a valid email address");
      setIsSubmitting(false);
      return;
    }

    trackEvent("email_submit_start", {
      goal: formData.goal,
      level: formData.level,
      equipment: formData.equipment,
      schedule: formData.schedule,
      user_tier: 'free'
    });

    try {
      // Step 1: Subscribe to Beehiiv
      const subscribeResponse = await fetch(API_ENDPOINTS.subscribe, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: formData.email,
          referring_site: "https://workoutaiplan.com",
          level: formData.level,
          equipment: formData.equipment,
          goal: formData.goal,
        }),
      });

      const subscribeData = await subscribeResponse.json();

      if (!subscribeResponse.ok || !subscribeData.success) {
        throw new Error(subscribeData.message || "Subscription failed");
      }

      if (subscribeData.subscriptionId) {
        localStorage.setItem("beehiiv_subscription_id", subscribeData.subscriptionId);
      }

      trackEvent("email_subscribed", {
        goal: formData.goal,
        level: formData.level,
        equipment: formData.equipment,
        schedule: formData.schedule,
      user_tier: 'pending'
    });

    trackMetaPixel("Lead", {
        content_name: "Workout Plan Email",
        content_category: "Fitness"
      });

      // Step 2: Save pending user to database
      const pendingResponse = await fetch(API_ENDPOINTS.savePendingUser, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: formData.email,
          goal: formData.goal,
          level: formData.level,
          equipment: formData.equipment,
          schedule: formData.schedule,
        }),
      });

      if (!pendingResponse.ok) {
        console.error("Failed to save pending user, continuing anyway");
      }

      // Step 3: Store quiz data for Stripe checkout
      localStorage.setItem("pending_quiz_data", JSON.stringify({
        email: formData.email,
        goal: formData.goal,
        level: formData.level,
        equipment: formData.equipment,
        schedule: formData.schedule,
      }));

      // Step 4: Show paywall page
      setShowPaywall(true);

    } catch (err) {
      console.error("Error:", err);
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // If showing paywall, render PaywallPage
  if (showPaywall) {
    return (
      <PaywallPage
        quizData={formData}
        onBack={() => setShowPaywall(false)}
      />
    );
  }

  // Success State (for paid users who regenerate)
  if (submitted) {
    return (
      <QuizContainer id="quiz-section">
        <Container maxWidth="sm">
          <Box sx={{ textAlign: "center", py: 4 }}>
            <SuccessIcon>
              <CheckCircle2 size={40} color="#ffffff" />
            </SuccessIcon>
            <SuccessTitle>Your Plan is Ready! 🎉</SuccessTitle>
            <SuccessText>
              Your personalized workout plan has been downloaded successfully!
            </SuccessText>
            <button
              onClick={() => {
                setSubmitted(false);
                setStep(1);
                setFormData({ goal: "", level: "", equipment: "", schedule: "", email: "" });
              }}
              style={{
                color: "#FF6B35",
                textDecoration: "underline",
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "0.875rem",
                fontFamily: "'Oswald', sans-serif",
                fontWeight: 500,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
              }}
            >
              Generate another plan
            </button>
          </Box>
        </Container>
      </QuizContainer>
    );
  }

  return (
    <QuizContainer id="quiz-section">
      <Container maxWidth="md">
        {/* Progress Bar */}
        <ProgressBar>
          <ProgressInfo>
            <span>Question {step} of {totalSteps}</span>
            <span>{Math.round(progress)}% complete</span>
          </ProgressInfo>
          <ProgressTrack>
            <ProgressFill progress={progress} />
          </ProgressTrack>
        </ProgressBar>

        {/* Step 1: Goal Selection */}
        {step === 1 && (
          <>
            <QuestionTitle>What's your primary fitness goal?</QuestionTitle>
            <QuestionSubtitle>We'll personalize your plan based on this</QuestionSubtitle>

            <OptionsGrid>
              {goals.map((goal) => {
                const Icon = goal.icon;
                const isSelected = formData.goal === goal.id;

                return (
                  <OptionButton
                    key={goal.id}
                    selected={isSelected}
                    onClick={() => handleGoalSelect(goal.id)}
                  >
                    <IconBox selected={isSelected}>
                      <Icon size={24} color={isSelected ? "#ffffff" : "#FF6B35"} />
                    </IconBox>
                    <Box sx={{ flex: 1 }}>
                      <OptionText selected={isSelected}>{goal.label}</OptionText>
                      <OptionDescription>{goal.description}</OptionDescription>
                    </Box>
                    {isSelected && (
                      <CheckIcon>
                        <svg fill="currentColor" viewBox="0 0 20 20" style={{ width: "100%", height: "100%" }}>
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </CheckIcon>
                    )}
                  </OptionButton>
                );
              })}
            </OptionsGrid>

            <ContinueHint>Select an option to continue →</ContinueHint>
          </>
        )}

        {/* Step 2: Level Selection */}
        {step === 2 && (
          <>
            <QuestionTitle>What's your current fitness level?</QuestionTitle>
            <QuestionSubtitle>This helps us set the right difficulty</QuestionSubtitle>

            <OptionsGrid>
              {levels.map((level) => {
                const Icon = level.icon;
                const isSelected = formData.level === level.id;

                return (
                  <OptionButton
                    key={level.id}
                    selected={isSelected}
                    onClick={() => handleLevelSelect(level.id)}
                  >
                    <IconBox selected={isSelected}>
                      <Icon size={24} color={isSelected ? "#ffffff" : "#FF6B35"} />
                    </IconBox>
                    <Box sx={{ flex: 1 }}>
                      <OptionText selected={isSelected}>{level.label}</OptionText>
                      <OptionDescription>{level.description}</OptionDescription>
                    </Box>
                    {isSelected && (
                      <CheckIcon>
                        <svg fill="currentColor" viewBox="0 0 20 20" style={{ width: "100%", height: "100%" }}>
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </CheckIcon>
                    )}
                  </OptionButton>
                );
              })}
            </OptionsGrid>

            <BackButton onClick={() => setStep(1)}>← Back</BackButton>
            <ContinueHint>Select an option to continue →</ContinueHint>
          </>
        )}

        {/* Step 3: Equipment Selection */}
        {step === 3 && (
          <>
            <QuestionTitle>What equipment do you have?</QuestionTitle>
            <QuestionSubtitle>We'll design exercises for your setup</QuestionSubtitle>

            <OptionsGrid>
              {equipmentOptions.map((equipment) => {
                const Icon = equipment.icon;
                const isSelected = formData.equipment === equipment.id;
                const isLocked = equipment.locked && !isPremiumUser;

                return (
                  <OptionButton
                    key={equipment.id}
                    selected={isSelected}
                    locked={isLocked}
                    onClick={() => !isLocked && handleEquipmentSelect(equipment.id)}
                  >
                    {isLocked && <ProBadge>Pro Only</ProBadge>}
                    {isLocked && (
                      <LockIconWrapper>
                        <Lock size={24} color="#FFD700" />
                      </LockIconWrapper>
                    )}
                    <IconBox selected={isSelected}>
                      <Icon size={24} color={isSelected ? "#ffffff" : "#FF6B35"} />
                    </IconBox>
                    <Box sx={{ flex: 1 }}>
                      <OptionText selected={isSelected}>{equipment.label}</OptionText>
                      <OptionDescription>{equipment.description}</OptionDescription>
                    </Box>
                    {isSelected && !isLocked && (
                      <CheckIcon>
                        <CheckCircle2 size={20} color="#FF6B35" />
                      </CheckIcon>
                    )}
                  </OptionButton>
                );
              })}
            </OptionsGrid>

            <BackButton onClick={() => setStep(2)}>← Back</BackButton>
            <ContinueHint>Select an option to continue →</ContinueHint>
          </>
        )}

        {/* Step 4: Schedule */}
        {step === 4 && (
          <>
            <QuestionTitle>How many days per week?</QuestionTitle>
            <QuestionSubtitle>Choose your training frequency</QuestionSubtitle>

            {isSubmitting ? (
              <Box sx={{ textAlign: "center", py: 6 }}>
                <Dumbbell
                  size={48}
                  color="#FF6B35"
                  style={{
                    animation: "spin 1s linear infinite",
                    marginBottom: "16px"
                  }}
                />
                <Box sx={{
                  color: "#ffffff",
                  fontSize: "1.25rem",
                  fontFamily: "'Oswald', sans-serif",
                  fontWeight: 500,
                  marginBottom: "8px"
                }}>
                  Generating Your Plan...
                </Box>
                <Box sx={{
                  color: "rgba(255, 255, 255, 0.6)",
                  fontSize: "0.875rem",
                  fontFamily: "'Oswald', sans-serif"
                }}>
                  This will only take a moment
                </Box>
              </Box>
            ) : (
              <>
                <OptionsGrid>
                  {scheduleOptions.map((schedule) => {
                    const Icon = schedule.icon;
                    const isSelected = formData.schedule === schedule.id;

                    return (
                      <OptionButton
                        key={schedule.id}
                        selected={isSelected}
                        onClick={() => handleScheduleSelect(schedule.id)}
                      >
                        <IconBox selected={isSelected}>
                          <Icon size={24} color={isSelected ? "#ffffff" : "#FF6B35"} />
                        </IconBox>
                        <Box sx={{ flex: 1 }}>
                          <OptionText selected={isSelected}>{schedule.label}</OptionText>
                          <OptionDescription>{schedule.description}</OptionDescription>
                        </Box>
                        {isSelected && (
                          <CheckIcon>
                            <CheckCircle2 size={20} color="#FF6B35" />
                          </CheckIcon>
                        )}
                      </OptionButton>
                    );
                  })}
                </OptionsGrid>

                <BackButton onClick={() => setStep(3)}>← Back</BackButton>
                <ContinueHint>Select an option to continue →</ContinueHint>
              </>
            )}
          </>
        )}

        {/* Step 5: Email Capture */}
        {step === 5 && (
          <>
            <QuestionTitle>Where should we send your program?</QuestionTitle>
            <QuestionSubtitle>Enter your email to unlock your personalized plan</QuestionSubtitle>

            <EmailFormCard>
              <form onSubmit={handleSubmit}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {error && (
                    <Alert severity="error" sx={{ mb: 1 }}>
                      {error}
                    </Alert>
                  )}

                  <Box>
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={(e) => {
                        const email = e.target.value;
                        setFormData({ ...formData, email });
                        setEmailError("");
                        if (email && email.includes('@') && email.includes('.')) {
                          if (!validateEmail(email)) {
                            setEmailError("Please enter a valid email address");
                          }
                        }
                      }}
                      onBlur={(e) => {
                        const email = e.target.value;
                        if (email && !validateEmail(email)) {
                          setEmailError("Please enter a valid email address");
                        }
                      }}
                      placeholder="john@example.com"
                      disabled={isSubmitting}
                      sx={{
                        borderColor: emailError ? "#f44336" : undefined,
                      }}
                    />
                    {emailError && (
                      <Box sx={{ color: "#f44336", fontSize: "0.875rem", marginTop: "4px" }}>
                        {emailError}
                      </Box>
                    )}
                  </Box>

                  <SubmitButton type="submit" disabled={isSubmitting || !formData.email || emailError}>
                    {isSubmitting ? (
                      <>
                        <Dumbbell size={20} className="spinning-icon" />
                        Processing...
                      </>
                    ) : (
                      <>
                        See My Program
                        <ArrowRight size={20} />
                      </>
                    )}
                  </SubmitButton>

                  <Box sx={{ color: "#6B7280", fontSize: "0.75rem", textAlign: "center" }}>
                    No spam • Unsubscribe anytime
                  </Box>
                </Box>
              </form>
            </EmailFormCard>

            <BackButton onClick={() => setStep(4)}>← Back</BackButton>
          </>
        )}
      </Container>
    </QuizContainer>
  );
}

export default QuizSection;
